import { Flex, Button, useToast } from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { FiClock, FiLogOut, FiBell, FiChevronsRight, FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { forceSignOut } from '../../../utils/authUtils';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface ActionButtonsProps {
  onSignOut: () => Promise<void>;
}

export const ActionButtons = ({ onSignOut }: ActionButtonsProps) => {
  const { t } = useTranslation(['v0/common', 'v0/me']);
  const navigate = useNavigate();
  const toast = useToast();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // 增强的登出处理函数
  const handleSignOut = useCallback(async () => {
    try {
      // 尝试使用传入的 onSignOut 函数
      await onSignOut();
    } catch (error) {
      console.error('使用传入的 onSignOut 函数失败，尝试强制登出:', error);
      // 如果失败，使用强制登出函数
      await forceSignOut(
        navigate,
        toast,
        t('v0/common:actions.logout'),
        t('v0/common:errors.logoutFailed', '退出失败')
      );
    }
  }, [onSignOut, navigate, toast, t]);

  // 检测 PWA 安装状态
  useEffect(() => {
    // 使用统一的平台工具检测
    import('../../../utils/platformUtils').then(({ isStandaloneMode }) => {
      setIsPwaInstalled(isStandaloneMode());
    });

    // 监听 beforeinstallprompt 事件
    const handler = (e: Event) => {
      e.preventDefault();
      console.log('ActionButtons: 捕获到 beforeinstallprompt 事件');
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  // 安装 PWA
  const handleInstallPwa = async () => {
    if (isPwaInstalled) {
      toast({
        title: t('v0/me:pwa.alreadyInstalled', '已安装'),
        description: t('v0/me:pwa.alreadyInstalledDesc', '应用已经安装在您的设备上'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (deferredPrompt) {
      try {
        // 显示安装提示
        deferredPrompt.prompt();

        // 等待用户响应
        const { outcome } = await deferredPrompt.userChoice;
        console.log('用户安装选择结果:', outcome);

        if (outcome === 'accepted') {
          toast({
            title: t('v0/me:pwa.installSuccess', '安装成功'),
            description: t('v0/me:pwa.installSuccessDesc', '应用已成功安装到您的设备'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          setIsPwaInstalled(true);
        }

        // 清除提示，确保只使用一次
        setDeferredPrompt(null);
      } catch (error) {
        console.error('PWA 安装过程中出错:', error);
        toast({
          title: t('v0/me:pwa.installError', '安装失败'),
          description: t('v0/me:pwa.installErrorDesc', '安装过程中出现错误，请稍后再试'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } else {
      // 针对 iOS 设备的特殊处理
      if ('standalone' in window.navigator && (window.navigator as any).standalone === false) {
        toast({
          title: t('v0/me:pwa.iosInstructions', 'iOS 安装说明'),
          description: t('v0/me:pwa.iosInstructionsDesc', '请点击分享按钮，然后选择"添加到主屏幕"'),
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('v0/me:pwa.notAvailable', '无法安装'),
          description: t('v0/me:pwa.notAvailableDesc', '您的浏览器可能不支持 PWA 安装，或应用已经安装'),
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Flex
      mt={4}
      justify="center"
      gap={3}
      flexWrap="wrap"
    >
      <Button
        leftIcon={<FiPlus />}
        colorScheme="teal"
        onClick={() => navigate('/projects')}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {t('v0/me:actions.manageProjects')}
      </Button>

      <Button
        leftIcon={<FiChevronsRight />}
        colorScheme="purple"
        variant="outline"
        onClick={() => navigate('/v1/')}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {t('v0/me:actions.tryV1', '尝试 v1')}
      </Button>

      <Button
        leftIcon={<FiClock />}
        variant="outline"
        onClick={() => navigate('/tasks')}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {t('v0/me:actions.manageTasks')}
      </Button>

      <Button
        leftIcon={<FiBell />}
        colorScheme="purple"
        variant="outline"
        onClick={() => navigate('/v0/notification-test')}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {t('v0/me:actions.testNotifications')}
      </Button>

      <Button
        leftIcon={<FiDownload />}
        colorScheme="blue"
        variant={isPwaInstalled ? "outline" : "solid"}
        onClick={handleInstallPwa}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {isPwaInstalled
          ? t('v0/me:pwa.installed', '已安装应用')
          : t('v0/me:pwa.install', '安装应用')}
      </Button>

      <Button
        leftIcon={<FiLogOut />}
        colorScheme="red"
        variant="ghost"
        onClick={handleSignOut}
        size="sm"
        borderRadius="full"
        fontWeight="medium"
      >
        {t('v0/common:actions.logout')}
      </Button>
    </Flex>
  );
};